import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import FileDownload from 'js-file-download';
import useQueryApiClient from 'utils/useQueryApiClient';

interface FarmOrder {
  order_id: string;
  farm_id: number | string;
}

interface UseFarmOrderHandlerProps {
  onComplete?: () => void;
}

const useFarmOrderHandler = ({ onComplete }: UseFarmOrderHandlerProps = {}) => {
  const intl = useIntl();
  const [currentOrder, setCurrentOrder] = useState<FarmOrder | null>(null);

  const downloadPdfFromResponse = useCallback((response: { file?: string; farmName?: string; data?: { file: string; farmName: string } }) => {
    const { file, farmName } = response?.data ?? response;
    if (!file) {
      return;
    }

    const filename = `${intl.formatMessage({ id: 'cart_summary.invoice' })} ${farmName || ''}_${dayjs().format('YYYY-MM-DD')}.pdf`;

    const cleanedBase64 = file.replace(/\s/g, '');
    const byteCharacters = atob(cleanedBase64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: 'application/pdf' });
    FileDownload(blob, filename);
  }, [intl]);

  const { appendData: pdfData, isLoading: pdfLoading } = useQueryApiClient({
    request: {
      url: '/api/v2/generate-pdf',
      method: 'POST',
      multipart: false,
    },
    onSuccess: downloadPdfFromResponse,
  });

  const downloadFulfillmentPdfFromResponse = useCallback((
    response: {
      file?: string;
      farmName?: string;
      invoiceNr?: string;
      fileName?: string;
      mimeType?: string;
      data?: { file: string; farmName: string; invoiceNr: string; fileName?: string; mimeType?: string };
    },
  ) => {
    const { file, farmName, invoiceNr, fileName, mimeType } = response?.data ?? response;
    if (!file) {
      return;
    }

    const fallbackFileName = `${intl.formatMessage({ id: 'cart_summary.invoice' })} ${farmName || ''}_${invoiceNr || ''}.pdf`;
    const resolvedFileName = fileName || fallbackFileName;
    const resolvedMimeType = mimeType || (resolvedFileName.endsWith('.zip') ? 'application/zip' : 'application/pdf');

    const cleanedBase64 = file.replace(/\s/g, '');
    const byteCharacters = atob(cleanedBase64);
    const byteArray = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteArray[i] = byteCharacters.charCodeAt(i);
    }

    const blob = new Blob([byteArray], { type: resolvedMimeType });
    FileDownload(blob, resolvedFileName);
  }, [intl]);

  const { appendData: fulfillmentPdfData, isLoading: fulfillmentPdfLoading } = useQueryApiClient({
    request: {
      url: '/api/v2/generate-fulfillment-pdf',
      method: 'POST',
      multipart: false,
    },
    onSuccess: downloadFulfillmentPdfFromResponse,
  });

  const handleDownloadClick = useCallback(async (orders: FarmOrder[]) => {
    orders.forEach((order) => {
      setCurrentOrder(order);
      pdfData({
        order_id: order.order_id,
        farm_id: order.farm_id,
      });
    })
    if (onComplete) onComplete();
  }, [pdfData, onComplete]);

  const handleDownloadFulfillmentInvoice = useCallback((fulfillmentId: number) => {
    fulfillmentPdfData({ fulfillment_id: fulfillmentId });
  }, [fulfillmentPdfData]);

  return {
    handleDownloadClick,
    handleDownloadFulfillmentInvoice,
    currentOrder,
    pdfLoading,
    fulfillmentPdfLoading,
  };
};

export default useFarmOrderHandler;
